import type { HexColorString } from '@fluent-theme-tools/color-operations';
import {
  ThemeMode,
  WCAG_2_1_AAA_CONTRAST_RATIO,
  createTheme,
  getAccessibleBrandRamp,
  getBrandVariants,
  getColorDifference,
  getContrastWCAG_2_1,
  getExactBrandRamp,
  getV9CompatibleBrandColor,
} from '@fluent-theme-tools/color-operations';
import type { Theme } from '@fluentui/react-components';

import { isValidHexColor } from '../../colors';

import { createCoreUIFluentTheme } from './createCoreUIFluentTheme';

export { ThemeMode };

const addHexPrefix = (color: string): HexColorString => `#${color}`;

/**
 * Converts a valid hex color string to HexColorString format
 * Ensures the hex color has a # prefix and is in the correct format
 * Normalizes 3-digit hex colors to 6-digit format for consistency
 * @param color - Hex color string (with or without # prefix)
 * @returns HexColorString in proper format
 * @throws Error if the color format is invalid
 */
const toHexColorString = (color: string): HexColorString => {
  // Add # prefix if missing
  const normalizedColor = addHexPrefix(color.startsWith('#') ? color.slice(1) : color);

  if (!isValidHexColor(normalizedColor)) {
    throw new Error(`Invalid hex color format: ${color}. Expected format: #000 or #000000 (3 or 6-digit hex)`);
  }

  // Expand 3-digit hex to 6-digit hex for consistency
  if (normalizedColor.length === 4) {
    const [, r, g, b] = normalizedColor;

    return `#${r}${r}${g}${g}${b}${b}`;
  }

  return normalizedColor;
};

type CustomFluent9ThemeType = 'exact' | 'accessible';
const createFluentThemeFromColor = (color: HexColorString, mode: ThemeMode): Theme => {
  const themeColor = toHexColorString(color);
  const brandRamp = getExactBrandRamp(themeColor);
  const theme = createTheme(mode, brandRamp);
  const brand = getBrandVariants(brandRamp);
  const themeWithDesignerDarkMapping =
    mode === ThemeMode.Dark
      ? {
          ...theme,
          colorBrandForeground1: brand[110],
          colorBrandForeground2: brand[120],
        }
      : theme;

  return themeWithDesignerDarkMapping;
};

const hasThemeDesignerContrastIssues = (theme: Theme): boolean => {
  const foreground1Contrast = getContrastWCAG_2_1(
    toHexColorString(theme.colorBrandForeground1),
    toHexColorString(theme.colorNeutralBackground1)
  );
  const foreground2Contrast = getContrastWCAG_2_1(
    toHexColorString(theme.colorBrandForeground2),
    toHexColorString(theme.colorBrandBackground2)
  );

  return foreground1Contrast < WCAG_2_1_AAA_CONTRAST_RATIO || foreground2Contrast < WCAG_2_1_AAA_CONTRAST_RATIO;
};

const getAccessibleThemeColor = (color: HexColorString): HexColorString => {
  const compatibleColor = getV9CompatibleBrandColor(color);
  const candidateColors = [compatibleColor, ...getAccessibleBrandRamp(compatibleColor).palette];
  const passingColors = candidateColors.filter((candidateColor) => {
    const lightTheme = createFluentThemeFromColor(candidateColor, ThemeMode.Light);
    const darkTheme = createFluentThemeFromColor(candidateColor, ThemeMode.Dark);

    return !hasThemeDesignerContrastIssues(lightTheme) && !hasThemeDesignerContrastIssues(darkTheme);
  });

  const nearestPassingColor = passingColors.reduce<HexColorString | undefined>((nearestColor, candidateColor) => {
    if (!nearestColor) {
      return candidateColor;
    }

    return getColorDifference(color, candidateColor) < getColorDifference(color, nearestColor)
      ? candidateColor
      : nearestColor;
  }, undefined);

  /* istanbul ignore next */
  if (!nearestPassingColor) {
    throw new Error(`Unable to generate an accessible Fluent 9 theme color for ${color}`);
  }

  return nearestPassingColor;
};

type CreateCustomFluent9Theme = (color: string, mode?: ThemeMode, type?: CustomFluent9ThemeType) => Theme;
export const createCustomFluent9Theme: CreateCustomFluent9Theme = (color, mode, type) => {
  const hexColorString = toHexColorString(color);
  const themeMode = mode ?? ThemeMode.Light;
  const themeType = type ?? 'accessible';
  const themeColor = themeType === 'exact' ? hexColorString : getAccessibleThemeColor(hexColorString);

  return createCoreUIFluentTheme(createFluentThemeFromColor(themeColor, themeMode));
};

interface CustomFluent9ThemeWithAccessibilityCheck {
  readonly accessibleDarkTheme: Theme;
  readonly accessibleLightTheme: Theme;
  readonly color: string;
  readonly colorAccessible: string;
  readonly exactDarkTheme: Theme;
  readonly exactLightTheme: Theme;
  readonly hasContrastIssues: boolean;
}
type CreateCustomFluent9ThemeWithAccessibilityCheck = (color: string) => CustomFluent9ThemeWithAccessibilityCheck;
export const createCustomFluent9ThemeWithAccessibilityCheck: CreateCustomFluent9ThemeWithAccessibilityCheck = (
  color
) => {
  const exactLightTheme = createCustomFluent9Theme(color, ThemeMode.Light, 'exact');
  const exactDarkTheme = createCustomFluent9Theme(color, ThemeMode.Dark, 'exact');
  const hasLightThemeContrastIssues = hasThemeDesignerContrastIssues(exactLightTheme);
  const hasDarkThemeContrastIssues = hasThemeDesignerContrastIssues(exactDarkTheme);
  const hasContrastIssues = hasLightThemeContrastIssues || hasDarkThemeContrastIssues;
  const exactColor = toHexColorString(color);
  const accessibleColor = hasContrastIssues ? getAccessibleThemeColor(exactColor) : exactColor;
  const accessibleLightTheme = hasContrastIssues
    ? createCoreUIFluentTheme(createFluentThemeFromColor(accessibleColor, ThemeMode.Light))
    : exactLightTheme;
  const accessibleDarkTheme = hasContrastIssues
    ? createCoreUIFluentTheme(createFluentThemeFromColor(accessibleColor, ThemeMode.Dark))
    : exactDarkTheme;

  return {
    color: exactLightTheme.colorBrandBackground,
    colorAccessible: accessibleLightTheme.colorBrandBackground,
    exactLightTheme,
    exactDarkTheme,
    accessibleLightTheme: hasContrastIssues ? accessibleLightTheme : exactLightTheme,
    accessibleDarkTheme: hasContrastIssues ? accessibleDarkTheme : exactDarkTheme,
    hasContrastIssues,
  };
};
